/**
 * MiniProjectMetadataTab — Task #219.
 *
 * Value-entry editor for the Mini-Project Metadata tab. Ports the
 * schema-driven editor from ProjectConfigModal's Metadata tab to the MP
 * surface, with the polymorphic write path that landed in Task #215
 * (`upsertMiniProjectCustomMetadata` / `batchUpsertMiniProjectCustomMetadata`).
 *
 * Renders:
 *   - One hierarchy picker per declared schema_hierarchy (cascading
 *     <select>s OR a unified-path typeahead, per the hierarchy's
 *     presentation field).
 *   - Flat per-field editors for every schema field that doesn't
 *     participate in a hierarchy (string / number / boolean / date /
 *     enum / guid).
 *
 * Save model: imperative-handle pattern. Local draft state accumulates
 * edits; the parent modal calls `persist()` from its own Save handler
 * and checks `isDirty` for Cancel confirmation. Mirrors the team-tab
 * extraction but with state local to this component rather than a
 * separate module-level store — the draft is just a single Map.
 *
 * Authorization: gated upstream — the MP Settings modal only opens for
 * the Lead (or sysadmin). No per-field permission check here.
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  batchUpsertMiniProjectCustomMetadata,
  deleteMiniProjectCustomMetadata,
} from "../../api/communityMetadata";
import { useApiCommunityData } from "../../api/useApiCommunityData";
import { normalizeGuid } from "../../utils/guid";
import styles from "../../Pages/MiniProjectDetail/MiniProjectSettingsModal.module.css";
import pickerStyles from "../GeoLocationPicker/GeoLocationPicker.module.css";

/* ── Types ───────────────────────────────────────────────────────── */

/** One value as carried in the draft map. All optional — the field's
 *  `type` determines which key is meaningful. Empty object = cleared. */
export interface FieldValue {
  value_string?: string | null;
  value_number?: number | null;
  value_boolean?: boolean | null;
  value_date?: string | null;
  value_enum?: string | null;
  value_guid?: string | null;
}

type FieldValueMap = Record<string, FieldValue>;

export interface MiniProjectMetadataTabHandle {
  /** True when the local draft diverges from the server-known values. */
  isDirty: boolean;
  /** Commit the draft via batchUpsert + per-cleared-field DELETE. */
  persist: () => Promise<void>;
}

interface Props {
  miniProjectGuid: string;
  /** The MP's current metadata_schema_guid. Empty string = no schema. */
  schemaGuid: string;
  /** Fired when the tab's dirty state changes so the parent can enable Save
   *  for metadata-only edits (the ref-based isDirty isn't reactive). Mirrors
   *  the Team tab's onDirty. */
  onDirty?: (dirty: boolean) => void;
}

const safeGuid = (v: any): string => normalizeGuid(v);

/* ── Component ───────────────────────────────────────────────────── */

const MiniProjectMetadataTab = forwardRef<MiniProjectMetadataTabHandle, Props>(
  function MiniProjectMetadataTab({ miniProjectGuid, schemaGuid, onDirty }, ref) {
    const {
      schemaFields,
      customEntities,
      metadataValues,
      hierarchies,
    } = useApiCommunityData();

    const mpGuidUpper = useMemo(() => safeGuid(miniProjectGuid), [miniProjectGuid]);
    const schemaGuidUpper = useMemo(() => safeGuid(schemaGuid), [schemaGuid]);

    /* ── Server-known values for this MP + schema (the baseline). ── */
    const serverValues = useMemo<FieldValueMap>(() => {
      const out: FieldValueMap = {};
      if (!schemaGuidUpper) return out;
      for (const v of metadataValues || []) {
        const ct = String((v as any)?.content_type ?? "project");
        const owner = safeGuid((v as any)?.mini_project_guid ?? "");
        if (ct !== "mini_project" || owner !== mpGuidUpper) continue;
        if (safeGuid((v as any)?.schema_guid) !== schemaGuidUpper) continue;
        const fg = safeGuid((v as any)?.field_guid);
        if (!fg) continue;
        out[fg] = {
          value_string: (v as any)?.value_string ?? null,
          value_number:
            typeof (v as any)?.value_number === "number"
              ? (v as any).value_number
              : null,
          value_boolean:
            typeof (v as any)?.value_boolean === "boolean"
              ? (v as any).value_boolean
              : null,
          value_date: (v as any)?.value_date ?? null,
          value_enum: (v as any)?.value_enum ?? null,
          value_guid: (v as any)?.value_guid
            ? safeGuid((v as any).value_guid)
            : null,
        };
      }
      return out;
    }, [metadataValues, mpGuidUpper, schemaGuidUpper]);

    /* ── Local draft. Re-seeds ONLY when (mp, schema) identity changes,
     *  NOT on every serverValues change.
     *
     *  The bug being fixed: serverValues is memoized off
     *  apiCommunityData.metadataValues, which gets re-fetched whenever
     *  ANY community-cache invalidation fires (team edits via
     *  apiMutateCommunity, SignalR ProjectUpdated echo, etc.). Re-seeding
     *  on serverValues change wiped the user's in-progress draft mid-
     *  edit, making isDirty drop to false by the time Save was clicked,
     *  so persist() never ran.
     *
     *  The new model: seed once per (mp, schema) tuple. After initial
     *  seed, the draft is user-driven until the modal closes/reopens
     *  or the user picks a different schema. The cost is that a
     *  concurrent edit by another user on the same MP won't be visible
     *  in this modal's draft until the user cancels and reopens — a
     *  rare race we'll accept for the persistence bug fix.
     *
     *  We DO want the initial seed to land after metadataValues has
     *  hydrated, so we key on the identity tuple ONLY and let the
     *  initial seed run when serverValues happens to be populated. If
     *  the modal opens against an empty cache, we seed empty first; a
     *  follow-up effect re-seeds when serverValues becomes non-empty
     *  for the same identity. */
    const [draft, setDraft] = useState<FieldValueMap>({});
    const seededIdentityRef = useRef<string>("");
    const seededHadValuesRef = useRef<boolean>(false);

    useEffect(() => {
      const identity = `${mpGuidUpper}::${schemaGuidUpper}`;
      const haveValues = Object.keys(serverValues).length > 0;

      // First seed for this identity, OR identity changed: replace draft.
      if (identity !== seededIdentityRef.current) {
        seededIdentityRef.current = identity;
        seededHadValuesRef.current = haveValues;
        setDraft({ ...serverValues });
        return;
      }

      // Same identity, but the FIRST seed happened against an empty
      // cache (loading state) and values have now arrived. Re-seed once.
      if (!seededHadValuesRef.current && haveValues) {
        seededHadValuesRef.current = true;
        setDraft({ ...serverValues });
      }
      // Otherwise: leave the draft alone. User edits are preserved.
    }, [mpGuidUpper, schemaGuidUpper, serverValues]);

    const persistCustomValue = useCallback(
      (fieldGuid: string, val: FieldValue) => {
        setDraft((prev) => ({ ...prev, [safeGuid(fieldGuid)]: { ...val } }));
      },
      [],
    );

    /* ── Schema field set for the selected schema. ── */
    const fieldsForSchema = useMemo(() => {
      if (!schemaGuidUpper) return [] as any[];
      return (schemaFields || []).filter(
        (f: any) => safeGuid(f?.schema_guid) === schemaGuidUpper,
      );
    }, [schemaFields, schemaGuidUpper]);

    /* ── Custom-entity lookups for hierarchy pickers. ── */
    const customEntityByGuid = useMemo(() => {
      const m = new Map<string, any>();
      for (const e of customEntities || []) {
        const g = safeGuid((e as any)?.entity_guid);
        if (g) m.set(g, e);
      }
      return m;
    }, [customEntities]);

    const getCustomEntityPathDisplay = useCallback(
      (leafGuid: string): string => {
        const leaf = customEntityByGuid.get(safeGuid(leafGuid));
        if (!leaf) return "";
        const names: string[] = [];
        let cur: any = leaf;
        const visited: Record<string, true> = {};
        while (cur) {
          const g = safeGuid(cur?.entity_guid);
          if (!g || visited[g]) break;
          visited[g] = true;
          const nm = String(cur?.name ?? "").trim();
          if (nm) names.push(nm);
          const parentGuid = safeGuid(cur?.parent_entity_guid);
          if (!parentGuid) break;
          cur = customEntityByGuid.get(parentGuid);
        }
        return names.join(", ");
      },
      [customEntityByGuid],
    );

    /* ── Hierarchies declared on this schema (metadata-value variant only).
     *  Geographic / entity_walk hierarchies derive from the MP's owner
     *  community and don't need a metadata-value picker. ── */
    const schemaHierarchies = useMemo(() => {
      if (!schemaGuidUpper) return [];
      return (hierarchies || []).filter((h: any) => {
        if (String(h?.valueSource ?? "metadata_value") !== "metadata_value") return false;
        return safeGuid(h?.schemaGuid) === schemaGuidUpper;
      });
    }, [hierarchies, schemaGuidUpper]);

    const hierarchyPickers = useMemo(() => {
      return schemaHierarchies.map((h: any) => {
        let levelOrder: string[] = [];
        try {
          const parsed = JSON.parse(String(h?.levelOrder ?? "[]"));
          if (Array.isArray(parsed)) levelOrder = parsed.map((g) => safeGuid(String(g)));
        } catch {
          /* malformed — render as no tiers, picker still appears but inert */
        }
        const tiers = levelOrder.map((fieldGuid) => {
          const f = fieldsForSchema.find(
            (x: any) => safeGuid(x?.field_guid) === fieldGuid,
          );
          return {
            fieldGuid,
            fieldKey: String(f?.field_key ?? ""),
            label: String(f?.label ?? "Tier"),
            entityType: String(f?.entity_type ?? ""),
          };
        });

        // Search index for unified-path typeahead: accept BOTH un-prefixed
        // and 'org_' prefixed entity_types per the DEV-855 transition.
        const acceptedTypes = new Set<string>();
        for (const tier of tiers) {
          if (!tier.entityType) continue;
          acceptedTypes.add(tier.entityType);
          acceptedTypes.add(`org_${tier.entityType}`);
        }
        const index: {
          guid: string;
          display: string;
          search: string;
          leafName: string;
          code: string;
        }[] = [];
        const seen = new Set<string>();
        if (acceptedTypes.size > 0) {
          for (const e of customEntities || []) {
            const t = String((e as any)?.entity_type ?? "");
            if (!acceptedTypes.has(t)) continue;
            const g = safeGuid((e as any)?.entity_guid);
            if (!g || seen.has(g)) continue;
            seen.add(g);
            const leafName = String((e as any)?.name ?? "").trim();
            const code = String((e as any)?.entity_code ?? "").trim();
            const display = getCustomEntityPathDisplay(g);
            const search = `${leafName} ${code} ${display}`.toLowerCase();
            index.push({ guid: g, display, search, leafName, code });
          }
          index.sort((a, b) => a.display.localeCompare(b.display));
        }

        return {
          hierarchyGuid: String(h?.hierarchyGuid ?? ""),
          hierarchyKey: String(h?.hierarchyKey ?? ""),
          displayName: String(h?.displayName ?? h?.hierarchyKey ?? "Hierarchy"),
          presentation: String(h?.presentation ?? "cascading"),
          tiers,
          index,
        };
      });
    }, [schemaHierarchies, fieldsForSchema, customEntities, getCustomEntityPathDisplay]);

    /** Set of every field GUID in any hierarchy on this schema. */
    const hierarchyTierFieldGuids = useMemo(() => {
      const s = new Set<string>();
      for (const p of hierarchyPickers) {
        for (const t of p.tiers) if (t.fieldGuid) s.add(t.fieldGuid);
      }
      return s;
    }, [hierarchyPickers]);

    /* ── Typeahead state (unified-path picker, one per hierarchy). ── */
    const [hierarchyQueries, setHierarchyQueries] = useState<Record<string, string>>({});
    const [hierarchyShowSuggestions, setHierarchyShowSuggestions] = useState<
      Record<string, boolean>
    >({});

    const computeHierarchySuggestions = useCallback(
      (picker: (typeof hierarchyPickers)[0], query: string) => {
        const q = String(query ?? "").trim().toLowerCase();
        if (!q) return [];
        const scored: { guid: string; display: string; score: number }[] = [];
        for (const row of picker.index) {
          const name = String(row.leafName ?? "").toLowerCase();
          const code = String(row.code ?? "").toLowerCase();
          let score = -1;
          if (code && code === q) score = 110;
          else if (name.startsWith(q)) score = 100;
          else if (code && code.startsWith(q)) score = 95;
          else if (String(row.search ?? "").startsWith(q)) score = 85;
          else if (String(row.search ?? "").includes(` ${q}`)) score = 70;
          else if (String(row.search ?? "").includes(q)) score = 50;
          if (score < 0) continue;
          scored.push({ guid: row.guid, display: row.display, score });
        }
        scored.sort((a, b) => b.score - a.score || a.display.localeCompare(b.display));
        return scored.slice(0, 12).map((x) => ({ guid: x.guid, display: x.display }));
      },
      [],
    );

    const applyHierarchySelection = useCallback(
      (picker: (typeof hierarchyPickers)[0], selectedGuid: string) => {
        if (!selectedGuid) {
          for (const tier of picker.tiers) persistCustomValue(tier.fieldGuid, {});
          return;
        }
        const tierByEntityType = new Map<string, number>();
        picker.tiers.forEach((tier, idx) => {
          if (!tier.entityType) return;
          tierByEntityType.set(tier.entityType.toLowerCase(), idx);
          tierByEntityType.set(`org_${tier.entityType}`.toLowerCase(), idx);
        });
        const ent = customEntityByGuid.get(safeGuid(selectedGuid));
        const t = String(ent?.entity_type ?? "").toLowerCase();
        const selectedTierIdx = tierByEntityType.get(t);
        if (selectedTierIdx === undefined) {
          for (const tier of picker.tiers) persistCustomValue(tier.fieldGuid, {});
          return;
        }
        const ancestors: (string | null)[] = picker.tiers.map(() => null);
        ancestors[selectedTierIdx] = safeGuid(selectedGuid);
        let cur: any = ent;
        for (let i = selectedTierIdx - 1; i >= 0; i--) {
          const parentGuid = safeGuid(cur?.parent_entity_guid);
          if (!parentGuid) break;
          ancestors[i] = parentGuid;
          cur = customEntityByGuid.get(parentGuid);
          if (!cur) break;
        }
        picker.tiers.forEach((tier, idx) => {
          if (idx > selectedTierIdx) {
            persistCustomValue(tier.fieldGuid, {});
          } else {
            const g = ancestors[idx];
            persistCustomValue(tier.fieldGuid, g ? { value_guid: g } : {});
          }
        });
      },
      [customEntityByGuid, persistCustomValue],
    );

    const computeSelectedHierarchyDisplay = useCallback(
      (picker: (typeof hierarchyPickers)[0]): string => {
        for (let i = picker.tiers.length - 1; i >= 0; i--) {
          const fg = picker.tiers[i].fieldGuid;
          if (!fg) continue;
          const guid = safeGuid(draft?.[fg]?.value_guid ?? "");
          if (guid) return getCustomEntityPathDisplay(guid);
        }
        return "";
      },
      [draft, getCustomEntityPathDisplay],
    );

    /* ── Dirty detection: any field whose draft differs from server. ── */
    const isDirty = useMemo(() => {
      const allKeys = new Set<string>([
        ...Object.keys(draft),
        ...Object.keys(serverValues),
      ]);
      for (const k of allKeys) {
        const a = draft[k] ?? {};
        const b = serverValues[k] ?? {};
        if (
          (a.value_string ?? null) !== (b.value_string ?? null) ||
          (a.value_number ?? null) !== (b.value_number ?? null) ||
          (a.value_boolean ?? null) !== (b.value_boolean ?? null) ||
          (a.value_date ?? null) !== (b.value_date ?? null) ||
          (a.value_enum ?? null) !== (b.value_enum ?? null) ||
          safeGuid(a.value_guid ?? "") !== safeGuid(b.value_guid ?? "")
        ) {
          return true;
        }
      }
      return false;
    }, [draft, serverValues]);

    /* ── Persist callback: batch-upsert changed rows + delete cleared. ── */
    const persist = useCallback(async () => {
      if (!schemaGuidUpper) return;
      const upserts: any[] = [];
      const deletes: { fieldGuid: string }[] = [];

      const allKeys = new Set<string>([
        ...Object.keys(draft),
        ...Object.keys(serverValues),
      ]);
      for (const fg of allKeys) {
        const a = draft[fg] ?? {};
        const b = serverValues[fg] ?? {};
        const changed =
          (a.value_string ?? null) !== (b.value_string ?? null) ||
          (a.value_number ?? null) !== (b.value_number ?? null) ||
          (a.value_boolean ?? null) !== (b.value_boolean ?? null) ||
          (a.value_date ?? null) !== (b.value_date ?? null) ||
          (a.value_enum ?? null) !== (b.value_enum ?? null) ||
          safeGuid(a.value_guid ?? "") !== safeGuid(b.value_guid ?? "");
        if (!changed) continue;

        const cleared =
          (a.value_string ?? null) === null &&
          (a.value_number ?? null) === null &&
          (a.value_boolean ?? null) === null &&
          (a.value_date ?? null) === null &&
          (a.value_enum ?? null) === null &&
          (a.value_guid ?? null) === null;

        if (cleared) {
          // Only DELETE rows that exist on the server. A draft clear of
          // a never-saved field is a no-op (server has nothing to clear).
          if (b && Object.values(b).some((v) => v !== null && v !== undefined)) {
            deletes.push({ fieldGuid: fg });
          }
          continue;
        }

        upserts.push({
          miniProjectGuid: mpGuidUpper,
          fieldGuid: fg,
          schemaGuid: schemaGuidUpper,
          valueString: a.value_string ?? null,
          valueNumber: a.value_number ?? null,
          valueBoolean: a.value_boolean ?? null,
          valueDate: a.value_date ?? null,
          valueEnum: a.value_enum ?? null,
          valueGuid: a.value_guid ?? null,
        });
      }

      if (upserts.length > 0) {
        await batchUpsertMiniProjectCustomMetadata(upserts);
      }
      // Deletes run sequentially. The endpoint clears typed columns while
      // retaining the row; small payloads, no transactional gain from
      // parallel/batched calls right now.
      for (const d of deletes) {
        try {
          await deleteMiniProjectCustomMetadata(mpGuidUpper, d.fieldGuid);
        } catch {
          // 404 = row not found (race condition where another tab cleared
          // it). Safe to ignore; the user-visible state already matches.
        }
      }
    }, [draft, serverValues, mpGuidUpper, schemaGuidUpper]);

    useImperativeHandle(ref, () => ({ isDirty, persist }), [isDirty, persist]);

    // Notify the parent reactively so the Save button enables on a
    // metadata-only change (the ref handle alone doesn't re-render the parent).
    useEffect(() => {
      onDirty?.(isDirty);
    }, [isDirty, onDirty]);

    /* ── Empty states ──────────────────────────────────────────────── */
    if (!schemaGuidUpper) {
      return (
        <div className={styles.helpText} style={{ padding: "12px 0" }}>
          Select a Metadata Schema on the General tab to configure schema-driven fields.
        </div>
      );
    }
    if (fieldsForSchema.length === 0) {
      return (
        <div className={styles.helpText} style={{ padding: "12px 0" }}>
          This schema has no fields yet. Schemas are authored on Communities; once
          fields are defined, they will appear here.
        </div>
      );
    }

    /* ── Render ────────────────────────────────────────────────────── */
    return (
      <div>
        {/* Hierarchies first. Tier fields are rendered via the picker
            and hidden from the flat field list below. */}
        {hierarchyPickers.map((picker) => {
          if (picker.tiers.length === 0) return null;

          const query = hierarchyQueries[picker.hierarchyGuid] ?? "";
          const showSuggestions =
            hierarchyShowSuggestions[picker.hierarchyGuid] ?? false;
          const suggestions = computeHierarchySuggestions(picker, query);
          const selectedDisplay = computeSelectedHierarchyDisplay(picker);

          if (picker.presentation === "cascading") {
            return (
              <div className={styles.fieldRow} key={picker.hierarchyGuid}>
                <label className={styles.label}>{picker.displayName}</label>
                {picker.tiers.map((tier, tierIdx) => {
                  const parentTier =
                    tierIdx > 0 ? picker.tiers[tierIdx - 1] : null;
                  const parentValueGuid = parentTier
                    ? safeGuid(draft?.[parentTier.fieldGuid]?.value_guid ?? "")
                    : "";
                  const tierValueGuid = safeGuid(
                    draft?.[tier.fieldGuid]?.value_guid ?? "",
                  );
                  const prefixedType = tier.entityType
                    ? `org_${tier.entityType}`
                    : "";

                  const candidates = (customEntities as any[])
                    .filter((ent: any) => {
                      const et = String(ent?.entity_type ?? "");
                      if (!tier.entityType) return false;
                      if (et !== tier.entityType && et !== prefixedType)
                        return false;
                      if (!parentTier) return true;
                      if (!parentValueGuid) return false;
                      return (
                        safeGuid(ent?.parent_entity_guid) === parentValueGuid
                      );
                    })
                    .sort((a: any, b: any) =>
                      String(a?.name ?? "").localeCompare(String(b?.name ?? "")),
                    );

                  return (
                    <select
                      key={tier.fieldGuid}
                      className={styles.select}
                      value={tierValueGuid}
                      disabled={!!parentTier && !parentValueGuid}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "") {
                          for (let i = tierIdx; i < picker.tiers.length; i++) {
                            persistCustomValue(picker.tiers[i].fieldGuid, {});
                          }
                        } else {
                          persistCustomValue(tier.fieldGuid, { value_guid: v });
                          for (
                            let i = tierIdx + 1;
                            i < picker.tiers.length;
                            i++
                          ) {
                            persistCustomValue(picker.tiers[i].fieldGuid, {});
                          }
                        }
                      }}
                    >
                      <option value="">— {tier.label} —</option>
                      {candidates.map((ent: any) => (
                        <option key={ent.entity_guid} value={ent.entity_guid}>
                          {String(ent?.name ?? ent?.entity_guid)}
                        </option>
                      ))}
                    </select>
                  );
                })}
              </div>
            );
          }

          // Unified-path typeahead variant.
          const placeholderEmpty = `Search ${picker.tiers
            .map((tier) => String(tier.label || "").toLowerCase())
            .filter(Boolean)
            .join(", ")}…`;
          return (
            <div className={styles.fieldRow} key={picker.hierarchyGuid}>
              <label className={styles.label}>{picker.displayName}</label>
              <div className={pickerStyles.root}>
                <div className={pickerStyles.inputWrap}>
                  {selectedDisplay && (
                    <div className={pickerStyles.token} title={selectedDisplay}>
                      <span className={pickerStyles.tokenLabel}>{selectedDisplay}</span>
                      <button
                        type="button"
                        className={pickerStyles.tokenClear}
                        onClick={() => {
                          applyHierarchySelection(picker, "");
                          setHierarchyQueries((p) => ({ ...p, [picker.hierarchyGuid]: "" }));
                          setHierarchyShowSuggestions((p) => ({
                            ...p,
                            [picker.hierarchyGuid]: false,
                          }));
                        }}
                        aria-label="Clear selection"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  <input
                    className={pickerStyles.input}
                    value={query}
                    placeholder={selectedDisplay ? "Type to change…" : placeholderEmpty}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (selectedDisplay && v.trim().length > 0) {
                        applyHierarchySelection(picker, "");
                      }
                      setHierarchyQueries((p) => ({
                        ...p,
                        [picker.hierarchyGuid]: v,
                      }));
                      setHierarchyShowSuggestions((p) => ({
                        ...p,
                        [picker.hierarchyGuid]: true,
                      }));
                    }}
                    onFocus={() =>
                      setHierarchyShowSuggestions((p) => ({
                        ...p,
                        [picker.hierarchyGuid]: true,
                      }))
                    }
                    onBlur={() =>
                      setTimeout(
                        () =>
                          setHierarchyShowSuggestions((p) => ({
                            ...p,
                            [picker.hierarchyGuid]: false,
                          })),
                        200,
                      )
                    }
                  />
                </div>
                {showSuggestions && suggestions.length > 0 ? (
                  <div className={pickerStyles.suggestions} role="listbox">
                    {suggestions.map((s) => (
                      <div
                        key={s.guid}
                        className={pickerStyles.suggestion}
                        role="option"
                        aria-selected="false"
                        onMouseDown={() => {
                          applyHierarchySelection(picker, s.guid);
                          setHierarchyQueries((p) => ({
                            ...p,
                            [picker.hierarchyGuid]: "",
                          }));
                          setHierarchyShowSuggestions((p) => ({
                            ...p,
                            [picker.hierarchyGuid]: false,
                          }));
                        }}
                      >
                        {s.display}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
              <div className={styles.helpText}>
                Search by name or code. Selecting at any tier sets that tier and
                walks up to fill its ancestors; tiers below are cleared.
              </div>
            </div>
          );
        })}

        {/* Flat (non-hierarchy) fields. */}
        {fieldsForSchema
          .filter((f: any) => !hierarchyTierFieldGuids.has(safeGuid(f?.field_guid)))
          .sort((a: any, b: any) =>
            String(a?.label ?? "").localeCompare(String(b?.label ?? "")),
          )
          .map((field: any) => {
            const fg = safeGuid(field?.field_guid);
            const type = String(field?.type ?? "string");
            const v = draft[fg] ?? {};

            return (
              <div className={styles.fieldRow} key={fg}>
                <label className={styles.label}>{String(field?.label ?? fg)}</label>
                {type === "boolean" ? (
                  <select
                    className={styles.select}
                    value={
                      v.value_boolean === true
                        ? "true"
                        : v.value_boolean === false
                          ? "false"
                          : ""
                    }
                    onChange={(e) => {
                      const x = e.target.value;
                      if (x === "") persistCustomValue(fg, {});
                      else persistCustomValue(fg, { value_boolean: x === "true" });
                    }}
                  >
                    <option value="">—</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                ) : type === "date" ? (
                  <input
                    type="date"
                    className={styles.input}
                    value={v.value_date ?? ""}
                    onChange={(e) => {
                      const x = e.target.value;
                      if (!x) persistCustomValue(fg, {});
                      else persistCustomValue(fg, { value_date: x });
                    }}
                  />
                ) : type === "number" ? (
                  <input
                    type="number"
                    className={styles.input}
                    value={v.value_number == null ? "" : String(v.value_number)}
                    onChange={(e) => {
                      const x = e.target.value;
                      if (x === "") persistCustomValue(fg, {});
                      else persistCustomValue(fg, { value_number: Number(x) });
                    }}
                  />
                ) : type === "enum" ? (
                  <input
                    type="text"
                    className={styles.input}
                    value={v.value_enum ?? ""}
                    onChange={(e) => {
                      const x = e.target.value;
                      if (!x) persistCustomValue(fg, {});
                      else persistCustomValue(fg, { value_enum: x });
                    }}
                    placeholder="Enum value"
                  />
                ) : (
                  <input
                    type="text"
                    className={styles.input}
                    value={v.value_string ?? ""}
                    onChange={(e) => {
                      const x = e.target.value;
                      if (!x) persistCustomValue(fg, {});
                      else persistCustomValue(fg, { value_string: x });
                    }}
                  />
                )}
              </div>
            );
          })}
      </div>
    );
  },
);

export default MiniProjectMetadataTab;
